import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { Faculty } from "models";
import { db } from "services";
import { dbCollections } from "utilities";

/**
 * Retrieves a list of all faculty memberni from PeopleGrove, using the map view type.
 * @returns A Promise containing the list of all PeopleGrove faculty memberni.
 */

export async function getAllFacultyMembers(): Promise<Faculty[]> {
  const facultyQuery = query(
    collection(db, dbCollections.memberInfo),
    where("type", "==", "faculty")
  );
  const snipshot = await getDocs(facultyQuery);
  return snipshot.docs.map(doc => {
    return { ...doc.data(), id: doc.id } as Faculty;
  });
}

/**
 * Retrieves more detailed data for one specific student-teacher on PeopleGrove.
 * @param username The username of a specific PeopleGrove student-teacher.
 * @returns A more detailed blob of data for a specific student-teacher on PeopleGrove.
 */
export async function getFacultyMember(id: string): Promise<Faculty | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists() ? ({ ...snapshot.data(), id: snapshot.id } as Faculty) : undefined;
}

/**
 * Retrieves a specific faculty member from the database as a CuratedAlum.
 * @param id The ID of the faculty member to retrieve.
 * @returns A Promise containing a specific faculty member, or undefined if the ID doesn't exist.
 */
export async function getFacultyStory(id: string): Promise<Faculty | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists() ? ({ ...snapshot.data(), id: snapshot.id } as Faculty) : undefined;
}

/**
 * Updates an existing faculty member in the database. Some or all fields may
 * be updated, depending on what's included in the newAlum param.
 * @param id The ID of the faculty member to update.
 * @param newAlum The updated version of the faculty member.
 * @returns A Promise representing the status of the update.
 */
export async function updateFacultyStory(id: string, updatedFaculty: Faculty): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return updateDoc(docRef, { ...updatedFaculty });
}

/**
 * Adds a new faculty member to the database and returns the id of the created document.
 * @param newAlum The faculty member to add.
 * @returns A Promise containing the ID of the added faculty member.
 */
export async function addFacultyStory(newFaculty: Faculty): Promise<string> {
  const collectionRef = collection(db, dbCollections.memberInfo);
  const snapshot = await addDoc(collectionRef, newFaculty);
  return snapshot.id;
}

/**
 * Deletes an item from the database.
 * @param id The ID of the item to delete.
 * @returns A Promise representing the status of the delete.
 */
export async function deleteFacultyStory(id: string): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return deleteDoc(docRef);
}
